/**
 * AI Data Cleanup Service
 *
 * Handles deletion of AI insights data (savings events and usage checks)
 * Used when users reset data or remove Gmail accounts
 */

import { supabase } from './supabaseClient';

/**
 * Get current user ID from Supabase auth
 */
const getCurrentUserId = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) throw new Error('User not authenticated');
  return user.id;
};

/**
 * Delete ALL AI insights data for the current user
 * Use this when user clicks "Reset All Data"
 *
 * @returns {Promise<Object>} Deletion results
 */
export const deleteAllAIInsightsData = async () => {
  try {
    const userId = await getCurrentUserId();
    console.log('🗑️ [AI Cleanup] Deleting ALL AI insights data for user:', userId);

    const results = {
      savingsEvents: 0,
      usageChecks: 0
    };

    // Delete all savings events
    const { data: deletedSavings, error: savingsError } = await supabase
      .from('savings_events')
      .delete()
      .eq('user_id', userId)
      .select();

    if (savingsError) {
      console.error('❌ [AI Cleanup] Error deleting savings events:', savingsError);
      // Don't throw - continue with other deletions
    } else {
      results.savingsEvents = deletedSavings?.length || 0;
      console.log(`✅ [AI Cleanup] Deleted ${results.savingsEvents} savings events`);
    }

    // Delete all usage checks
    const { data: deletedChecks, error: checksError } = await supabase
      .from('subscription_usage_checks')
      .delete()
      .eq('user_id', userId)
      .select();

    if (checksError) {
      console.error('❌ [AI Cleanup] Error deleting usage checks:', checksError);
    } else {
      results.usageChecks = deletedChecks?.length || 0;
      console.log(`✅ [AI Cleanup] Deleted ${results.usageChecks} usage checks`);
    }

    console.log('🎉 [AI Cleanup] All AI insights data deleted:', results);
    return { success: true, results };

  } catch (error) {
    console.error('❌ [AI Cleanup] deleteAllAIInsightsData error:', error);
    throw error;
  }
};

/**
 * Delete AI insights data for subscriptions from a specific Gmail account
 * Use this when user removes a Gmail account
 *
 * @param {string} gmailEmail - The Gmail email address
 * @returns {Promise<Object>} Deletion results
 */
export const deleteAIInsightsForGmail = async (gmailEmail) => {
  try {
    const userId = await getCurrentUserId();
    console.log('🗑️ [AI Cleanup] Deleting AI insights data for Gmail:', gmailEmail);

    const results = {
      savingsEvents: 0,
      usageChecks: 0
    };

    // First, get all subscription IDs imported from this Gmail
    const { data: subscriptions, error: subsError } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('user_id', userId)
      .eq('imported_from_email', gmailEmail);

    if (subsError) {
      console.error('❌ [AI Cleanup] Error fetching subscriptions:', subsError);
      throw subsError;
    }

    const subscriptionIds = subscriptions?.map(sub => sub.id) || [];
    console.log(`📋 [AI Cleanup] Found ${subscriptionIds.length} subscriptions from Gmail: ${gmailEmail}`);

    if (subscriptionIds.length === 0) {
      console.log('ℹ️  [AI Cleanup] No subscriptions found, nothing to delete');
      return { success: true, results };
    }

    // Delete savings events for these subscriptions
    const { data: deletedSavings, error: savingsError } = await supabase
      .from('savings_events')
      .delete()
      .eq('user_id', userId)
      .in('subscription_id', subscriptionIds)
      .select();

    if (savingsError) {
      console.error('❌ [AI Cleanup] Error deleting savings events:', savingsError);
    } else {
      results.savingsEvents = deletedSavings?.length || 0;
      console.log(`✅ [AI Cleanup] Deleted ${results.savingsEvents} savings events`);
    }

    // Delete usage checks for these subscriptions
    const { data: deletedChecks, error: checksError } = await supabase
      .from('subscription_usage_checks')
      .delete()
      .eq('user_id', userId)
      .in('subscription_id', subscriptionIds)
      .select();

    if (checksError) {
      console.error('❌ [AI Cleanup] Error deleting usage checks:', checksError);
    } else {
      results.usageChecks = deletedChecks?.length || 0;
      console.log(`✅ [AI Cleanup] Deleted ${results.usageChecks} usage checks`);
    }

    console.log('🎉 [AI Cleanup] Gmail AI insights data deleted:', results);
    return { success: true, results };

  } catch (error) {
    console.error('❌ [AI Cleanup] deleteAIInsightsForGmail error:', error);
    throw error;
  }
};

/**
 * Get stats about AI insights data (for confirmation dialogs)
 *
 * @returns {Promise<Object>} Stats object
 */
export const getAIInsightsStats = async () => {
  try {
    const userId = await getCurrentUserId();

    const stats = {
      savingsEvents: 0,
      usageChecks: 0,
      totalLifetimeSavings: 0
    };

    // Count savings events
    const { count: savingsCount, error: savingsError } = await supabase
      .from('savings_events')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (!savingsError) {
      stats.savingsEvents = savingsCount || 0;
    }

    // Count usage checks
    const { count: checksCount, error: checksError } = await supabase
      .from('subscription_usage_checks')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (!checksError) {
      stats.usageChecks = checksCount || 0;
    }

    return stats;

  } catch (error) {
    console.error('❌ [AI Cleanup] getAIInsightsStats error:', error);
    return {
      savingsEvents: 0,
      usageChecks: 0,
      totalLifetimeSavings: 0
    };
  }
};

export default {
  deleteAllAIInsightsData,
  deleteAIInsightsForGmail,
  getAIInsightsStats
};
